const DEFAULT_MAX_BREADCRUMBS = 100;
const DEFAULT_NORMALIZE_DEPTH = 5;

export const COMMON_SENTRY_IGNORE_ERRORS = [
  "ResizeObserver loop completed with undelivered notifications.",
  "ResizeObserver loop limit exceeded",
  // User-aborted navigations/fetches — not actionable errors
  "AbortError",
  "The user aborted a request.",
  // Transient network blips — too noisy and unactionable
  "Network request failed",
  "NetworkError when attempting to fetch resource.",
  "Failed to fetch",
  // Lazy-chunk load failures after a deploy (next page load picks up new bundle)
  "ChunkLoadError",
  "Loading chunk",
  "Loading CSS chunk",
  // Browser extension interference
  "Non-Error promise rejection captured with value: Object Not Found Matching Id",
] as const;

/**
 * Mutates a Sentry event in-place to redact PII before it is sent.
 * Call this from a `beforeSend` callback in each app's `Sentry.init`.
 * The function accepts a structurally-typed subset so it works across
 * @sentry/node, @sentry/react, and @sentry/electron/renderer without
 * importing SDK-specific event types.
 */
function scrubKeys(obj?: Record<string, unknown>): void {
  if (!obj) return;
  for (const k of Object.keys(obj)) {
    if (/token|secret|password|api[_-]?key/i.test(k)) obj[k] = "[redacted]";
  }
}

export function scrubSentryEvent(event: {
  request?: { url?: string };
  extra?: Record<string, unknown>;
  tags?: Record<string, unknown>;
}): void {
  if (event.request?.url) {
    event.request.url = event.request.url.replace(
      /([?&](?:code|token|access_token|state)=)[^&]+/gi,
      "$1[redacted]",
    );
  }
  scrubKeys(event.extra);
  scrubKeys(event.tags);
}

export interface ResolveSentryConfigInput {
  readonly appName: string;
  readonly runtime: string;
  readonly defaultEnvironment: string;
  readonly defaultRelease?: string | null | undefined;
  readonly defaultDebug?: boolean | undefined;
  readonly defaultTracesSampleRate?: number | undefined;
  readonly defaultProfilesSampleRate?: number | undefined;
  readonly defaultReplaysSessionSampleRate?: number | undefined;
  readonly defaultReplaysOnErrorSampleRate?: number | undefined;
  /**
   * Whether Sentry should be active when `enabled` is not explicitly set via
   * an env var. Pass `true` only in production deployments. Defaults to `false`
   * so local dev and test environments never send events without opting in.
   */
  readonly defaultEnabled?: boolean | undefined;
  readonly enabled?: boolean | string | null | undefined;
  readonly dsn?: string | null | undefined;
  readonly environment?: string | null | undefined;
  readonly release?: string | null | undefined;
  readonly debug?: boolean | string | null | undefined;
  readonly tracesSampleRate?: number | string | null | undefined;
  readonly profilesSampleRate?: number | string | null | undefined;
  readonly replaysSessionSampleRate?: number | string | null | undefined;
  readonly replaysOnErrorSampleRate?: number | string | null | undefined;
  readonly tags?: Record<string, string | null | undefined>;
}

export interface BuildSentryReleaseInput {
  readonly appName: string;
  readonly version: string;
  readonly commitHash?: string | null | undefined;
}

export interface ResolvedSentryConfig {
  readonly dsn: string;
  readonly environment: string;
  readonly release?: string | undefined;
  readonly debug: boolean;
  readonly tracesSampleRate: number;
  readonly profilesSampleRate?: number | undefined;
  readonly replaysSessionSampleRate?: number | undefined;
  readonly replaysOnErrorSampleRate?: number | undefined;
  readonly attachStacktrace: true;
  readonly maxBreadcrumbs: number;
  readonly normalizeDepth: number;
  readonly tags: Readonly<Record<string, string>>;
}

export type SentryEnvSource = Record<string, string | undefined>;

export function buildSentryRelease(input: BuildSentryReleaseInput): string {
  const version = normalizeOptionalString(input.version) ?? "0.0.0";
  const commitHash = normalizeOptionalString(input.commitHash);
  return commitHash ? `${input.appName}@${version}+${commitHash}` : `${input.appName}@${version}`;
}

export function readFirstEnvValue(
  env: SentryEnvSource,
  keys: ReadonlyArray<string>,
): string | undefined {
  for (const key of keys) {
    const value = normalizeOptionalString(env[key]);
    if (value) {
      return value;
    }
  }

  return undefined;
}

/**
 * Whether a runtime intends to report errors, independent of whether a DSN is
 * actually configured.
 *
 * `resolveSentryConfig` deliberately collapses "no DSN" and "switched off" into a
 * single `null`, which is what let a missing production DSN disable reporting
 * silently. Callers that need to tell those two apart — to complain about the
 * first while staying quiet about the second — must ask the same question the
 * resolver asks, so this is the one implementation of it.
 */
export function resolveSentryEnabled(input: {
  readonly enabled?: boolean | string | null | undefined;
  readonly defaultEnabled?: boolean | undefined;
}): boolean {
  return normalizeBoolean(input.enabled) ?? input.defaultEnabled ?? false;
}

export function resolveSentryConfig(input: ResolveSentryConfigInput): ResolvedSentryConfig | null {
  const dsn = normalizeOptionalString(input.dsn);
  if (!dsn) {
    return null;
  }

  if (resolveSentryEnabled(input) === false) {
    return null;
  }

  const environment =
    normalizeOptionalString(input.environment) ??
    normalizeOptionalString(input.defaultEnvironment) ??
    "production";
  const release =
    normalizeOptionalString(input.release) ??
    normalizeOptionalString(input.defaultRelease) ??
    undefined;
  const debug = normalizeBoolean(input.debug) ?? input.defaultDebug ?? false;

  return {
    dsn,
    environment,
    release,
    debug,
    tracesSampleRate: normalizeSampleRate(
      input.tracesSampleRate,
      input.defaultTracesSampleRate ?? 0.1,
    ),
    profilesSampleRate: normalizeOptionalSampleRate(
      input.profilesSampleRate,
      input.defaultProfilesSampleRate,
    ),
    replaysSessionSampleRate: normalizeOptionalSampleRate(
      input.replaysSessionSampleRate,
      input.defaultReplaysSessionSampleRate,
    ),
    replaysOnErrorSampleRate: normalizeOptionalSampleRate(
      input.replaysOnErrorSampleRate,
      input.defaultReplaysOnErrorSampleRate,
    ),
    attachStacktrace: true,
    maxBreadcrumbs: DEFAULT_MAX_BREADCRUMBS,
    normalizeDepth: DEFAULT_NORMALIZE_DEPTH,
    tags: buildTags(input),
  };
}

function buildTags(input: ResolveSentryConfigInput): Readonly<Record<string, string>> {
  const tags: Record<string, string> = {
    app: input.appName,
    runtime: input.runtime,
  };

  for (const [key, value] of Object.entries(input.tags ?? {})) {
    const normalized = normalizeOptionalString(value);
    if (normalized) {
      tags[key] = normalized;
    }
  }

  return tags;
}

function normalizeOptionalString(value: string | null | undefined): string | null {
  if (value === null || value === undefined) {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function normalizeBoolean(value: boolean | string | null | undefined): boolean | undefined {
  if (typeof value === "boolean") {
    return value;
  }

  const normalized = normalizeOptionalString(value);
  if (!normalized) {
    return undefined;
  }

  if (normalized === "1" || normalized.toLowerCase() === "true") {
    return true;
  }

  if (normalized === "0" || normalized.toLowerCase() === "false") {
    return false;
  }

  return undefined;
}

function normalizeOptionalSampleRate(
  value: number | string | null | undefined,
  defaultValue: number | undefined,
): number | undefined {
  if (defaultValue === undefined && value === undefined) {
    return undefined;
  }

  return normalizeSampleRate(value, defaultValue ?? 0);
}

function normalizeSampleRate(
  value: number | string | null | undefined,
  defaultValue: number,
): number {
  const normalized = normalizeNumberish(value);
  if (normalized === null || normalized < 0 || normalized > 1) {
    return defaultValue;
  }

  return normalized;
}

function normalizeNumberish(value: number | string | null | undefined): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const normalized = normalizeOptionalString(value);
  if (!normalized) {
    return null;
  }

  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}
